import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, MenuItem, TextField,
  Grid, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import api from '../services/api';
import ExpandableTable from '../components/ExpandableTable';
import Layout from '../components/Layout';

const CATEGORIES = ['general', 'permits', 'plans', 'contracts', 'invoices', 'photos', 'inspection', 'other'];

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function Documents() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [docs, setDocs] = useState([]);
  const [viewDoc, setViewDoc] = useState({ open: false, doc: null });
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'general', description: '', file: null });

  useEffect(() => {
    api.get('/projects').then(r => {
      setProjects(r.data || []);
      if (r.data && r.data.length && !projectId) setProjectId(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get('/documents', { params: { project_id: projectId } })
      .then(r => setDocs(r.data || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const reload = () => {
    if (!projectId) return;
    api.get('/documents', { params: { project_id: projectId } })
      .then(r => setDocs(r.data || [])).catch(() => {});
  };

  const handleUpload = async () => {
    if (!form.file || !projectId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post('/documents', {
        project_id: projectId,
        name: form.name || up.data.filename,
        file_url: up.data.url,
        file_type: form.file.type,
        file_size: up.data.size,
        category: form.category,
        description: form.description
      });
      setUploadOpen(false);
      setForm({ name: '', category: 'general', description: '', file: null });
      reload();
    } catch (e) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await api.delete('/documents/' + id); reload(); }
    catch (e) { alert('Delete failed'); }
  };

  return (
    <Layout>
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>Project Documents</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>{docs.length} document{docs.length === 1 ? '' : 's'}</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField select label="Project" value={projectId}
          onChange={e => setProjectId(e.target.value)} sx={{ minWidth: 280 }} size="small">
          {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        <Button variant="contained" startIcon={<CloudUploadIcon />}
          onClick={() => setUploadOpen(true)} disabled={!projectId}
          sx={{ bgcolor: '#1a237e', borderRadius: 2 }}>Upload Document</Button>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : docs.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#999', py: 4 }}>No documents for this project yet.</Typography>
      ) : (
        <>
        <ExpandableTable
          rows={docs.slice().sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))}
          emptyMessage="No documents for this project yet."
          columns={[
            {
              key: 'name',
              label: 'Name',
              render: (d) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <DescriptionIcon sx={{ color: '#1a237e', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }} noWrap>{d.name}</Typography>
                </Box>
              )
            },
            {
              key: 'category',
              label: 'Category',
              width: 140,
              render: (d) => (
                <Chip label={d.category || 'general'} size="small"
                  sx={{ bgcolor: '#e8eaf6', color: '#1a237e', fontWeight: 600, height: 22, fontSize: 11, textTransform: 'capitalize' }} />
              )
            },
            {
              key: 'type',
              label: 'Type',
              width: 90,
              render: (d) => {
                const ext = (d.name || '').split('.').pop().toUpperCase();
                return <Typography variant="caption" sx={{ color: '#666' }}>{ext.length <= 5 ? ext : '\u2014'}</Typography>;
              }
            },
            {
              key: 'file_size',
              label: 'Size',
              width: 90,
              align: 'right',
              render: (d) => <Typography variant="caption" sx={{ color: '#666' }}>{formatSize(d.file_size)}</Typography>
            },
            {
              key: 'created_at',
              label: 'Date',
              width: 110,
              render: (d) => <Typography variant="caption" sx={{ color: '#666' }}>{d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}</Typography>
            }
          ]}
          renderExpanded={(d) => (
            <Box>
              <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
                {d.uploaded_by_name && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Uploaded by</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.uploaded_by_name}</Typography>
                  </Box>
                )}
                {d.description && (
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Description</Typography>
                    <Typography variant="body2">{d.description}</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start', borderTop: '1px solid #f0f0f0', pt: 1.5 }}>
                <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                  onClick={() => setViewDoc({ open: true, doc: d })}
                  sx={{ borderRadius: 2, fontSize: 11 }}>
                  View
                </Button>
                <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
                  href={d.file_url} target="_blank" rel="noopener noreferrer"
                  sx={{ borderRadius: 2, fontSize: 11 }}>
                  Open in new tab
                </Button>
                <Button size="small" variant="outlined" startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(d.id)}
                  sx={{ borderRadius: 2, fontSize: 11, borderColor: '#c62828', color: '#c62828' }}>
                  Delete
                </Button>
              </Box>
            </Box>
          )}
        />

      {/* In-app document preview dialog */}
      <Dialog open={viewDoc.open} onClose={() => setViewDoc({ open: false, doc: null })}
        maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1a237e', color: 'white', fontWeight: 700, pr: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700 }}>{viewDoc.doc?.name}</Typography>
            {viewDoc.doc?.category && (
              <Typography variant="caption" sx={{ color: '#bbb' }}>{viewDoc.doc.category}</Typography>
            )}
          </Box>
          <IconButton onClick={() => setViewDoc({ open: false, doc: null })} sx={{ color: 'white' }}>
            <Box component="span" sx={{ fontSize: 20 }}>&times;</Box>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {viewDoc.doc?.file_url ? (
            <iframe src={viewDoc.doc.file_url} title="Document"
              style={{ width: '100%', height: '100%', border: 'none', minHeight: '70vh' }} />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: '#666' }}><Typography>No file available.</Typography></Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          {viewDoc.doc?.file_url && (
            <Button variant="outlined" href={viewDoc.doc.file_url} target="_blank" sx={{ borderRadius: 2 }}>
              Open in new tab
            </Button>
          )}
          <Button variant="contained" onClick={() => setViewDoc({ open: false, doc: null })} sx={{ bgcolor: '#1a237e', borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Upload Document</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Document Name" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} margin="normal"
            placeholder="Leave blank to use file name" />
          <TextField fullWidth select label="Category" value={form.category}
            onChange={e => setForm({ ...form, category: e.target.value })} margin="normal">
            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Description" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })} margin="normal"
            multiline rows={2} />
          <Button variant="outlined" component="label" sx={{ mt: 2 }}>
            {form.file ? form.file.name : 'Choose File'}
            <input type="file" hidden onChange={e => setForm({ ...form, file: e.target.files[0] })} />
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload}
            disabled={!form.file || uploading}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Layout>
  );
}
